import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSetAtom } from 'jotai';
import type {
  QueryObserverResult,
  RefetchOptions,
} from '@tanstack/react-query';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box, Button, SelectChangeEvent, Stack } from '@mui/material';
import type { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';

import {
  AvailableFiltersSchema,
  HPOSchema,
  PatientHPOListsSchema,
  SavedFilterResults,
} from '@/zod_schemas';
import type { AnnotationSource } from '@/api/annotationSource';
import type { FiltersState } from './types';

import { variantFilterAtom } from './variantFilterAtom';
import { getHPOColumns } from '../Patient/components/hpo/getHPOColumns';
import { useGetHpo } from '@/api/hpo/hpo';

import AnnotationSourcesSelector from './AnnotationSourcesSelector';
import FilterType from './FilterType';
import HPOFilter from './HPOFilter';
import GeneFilter from './Genes/GeneFilter';

import { buildFrom, getNonEmptyValues } from '@/utils/filter';
import useDebouncedValue from '@/hooks/useDebouncedValue';

interface FilterPanelProps {
  availableFilters: AvailableFiltersSchema;
  patientHpoList: PatientHPOListsSchema;
  sourcesLoading: boolean;
  annotationSources: AnnotationSource | AnnotationSource[];
  setFinalFilters: Dispatch<SetStateAction<FiltersState>>;
  setSelectedRows: Dispatch<SetStateAction<GridRowSelectionModel>>;
  setSelectedVariantRows: Dispatch<SetStateAction<GridRowSelectionModel>>;
  selectedVariantRows: GridRowSelectionModel;
  refchPatientHPO: (options?: RefetchOptions | undefined) => Promise<
    QueryObserverResult<
      {
        hpo: {
          name: string;
          hpo_id: string;
          gene: string[];
        }[];
        patient_id: string;
      },
      Error
    >
  >;
}

const FilterPanel = ({
  availableFilters,
  sourcesLoading,
  annotationSources,
  setFinalFilters,
  patientHpoList,
  setSelectedVariantRows,
  setSelectedRows,
  selectedVariantRows,
  refchPatientHPO,
}: FilterPanelProps) => {
  // RHF context (no prop drilling)
  const { setValue, register, control, getValues, reset } =
    useFormContext<SavedFilterResults>();

  // UI local state
  const [open, setOpen] = useState(false);

  // HPO search state + debounce
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 300);

  const { data: fetchedData } = useGetHpo(
    debouncedSearchQuery.length >= 3 ? debouncedSearchQuery : undefined,
  );

  // Derived defaults (memoized)
  const dynamicVCFFilters = useMemo(
    () => buildFrom(availableFilters.vcf_filters),
    [availableFilters.vcf_filters],
  );

  const dynamicAnnotationFilters = useMemo(
    () => buildFrom(availableFilters.annotation_filters),
    [availableFilters.annotation_filters],
  );

  // Watch only the specific fields we care about

  const watchVariantFilters =
    useWatch<SavedFilterResults, 'request_filters.variant_selection'>({
      name: 'request_filters.variant_selection',
    }) ?? 0;

  const watchSelectedSources =
    useWatch<SavedFilterResults, 'request_filters.sources'>({
      name: 'request_filters.sources',
    }) ?? [];

  // Sync to atom
  const setVariantFilter = useSetAtom(variantFilterAtom);
  useEffect(() => {
    setVariantFilter(watchVariantFilters);
  }, [setVariantFilter, watchVariantFilters]);

  // Handlers
  const handleSourceChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value as string[];
    setValue('request_filters.sources', value);
  };

  const columnsResponse: GridColDef<HPOSchema>[] = useMemo(
    () => getHPOColumns({}),
    [],
  );

  const handleSearchChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setSearchQuery(event.target.value);
  };

  const handleSearchClick = (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | React.KeyboardEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();
    if (fetchedData?.termData?.terms) {
      // setHpoRows(fetchedData.termData.terms);
    }
  };

  const searchFieldRef = useRef<HTMLInputElement>(null);
  const focusSearchField = () => searchFieldRef.current?.focus();

  // Apply / Reset
  const onApply = () => {
    const v = getValues(); // read once

    const vcfFilter = getNonEmptyValues(v.request_filters.filters.vcf_filters);
    const annotationFilter = getNonEmptyValues(
      v.request_filters.filters.annotation_filters,
    );
    const geneInclude = v.request_filters.filters.gene_filters.include;
    const genePanelIds = v.request_filters.filters.gene_panel_ids;

    setFinalFilters({
      sources: watchSelectedSources,
      selected_only: v.request_filters.selected_only,
      variant_selection: v.request_filters.variant_selection,
      filters: {
        vcf_filters: vcfFilter,
        annotation_filters: annotationFilter,
        hpo_filters: v.request_filters.filters.hpo_filters,
        gene_filters: { exclude: [], include: geneInclude },
        gene_panel_ids: genePanelIds,
      },
    });

    setSelectedRows([]);
  };

  const onReset = () => {
    // Reset form to dynamic defaults from server-provided filter list
    reset({
      request_filters: {
        filters: {
          vcf_filters: dynamicVCFFilters,
          annotation_filters: dynamicAnnotationFilters,
          hpo_filters: [],
          gene_filters: { include: [] },
          gene_panel_ids: [],
        },
        sources: [],
        variant_selection: 0,
        selected_only: false,
      },
    });

    // Reset externally tracked final filters
    setFinalFilters({
      sources: [],
      filters: { vcf_filters: {}, annotation_filters: {} },
      variant_selection: 0,
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: 400,
        bgcolor: 'background.default',
        overflow: 'hidden',
      }}
    >
      {/* Scrollable content */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          minHeight: 0,
          pr: 1,
          pt: 2.5,
        }}
      >
        <AnnotationSourcesSelector
          typographyText="Annotation Sources"
          annotationSources={annotationSources}
          watchSelectedSources={watchSelectedSources}
          handleSourceChange2={handleSourceChange}
          sourcesLoading={sourcesLoading}
        />

        <FilterType
          typographyText="VCF Filters"
          type="vcf_filters"
          register={register}
          control={control}
          availableFilters={availableFilters}
        />

        <FilterType
          typographyText="Annotation Filters"
          type="annotation_filters"
          register={register}
          control={control}
          availableFilters={availableFilters}
        />

        <HPOFilter
          setSelectedVariantRows={setSelectedVariantRows}
          setOpen={setOpen}
          patientHpoList={patientHpoList}
          open={open}
          searchQuery={searchQuery}
          handleSearchChange={handleSearchChange}
          handleSearchClick={handleSearchClick}
          searchFieldRef={searchFieldRef}
          focusSearchField={focusSearchField}
          selectedVariantRows={selectedVariantRows}
          fetchedData={fetchedData}
          columnsResponse={columnsResponse}
          refchPatientHPO={refchPatientHPO}
        />

        <GeneFilter control={control} />
      </Box>

      {/* Footer actions */}
      <Box
        sx={{
          p: 1,
          mr: 0.9,
          mb: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        <Stack direction="row" spacing={1} justifyContent="space-between">
          <Button variant="contained" onClick={onApply}>
            Apply
          </Button>
          <Button variant="outlined" color="secondary" onClick={onReset}>
            Reset
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default FilterPanel;
